<template>
  <div v-if="isThisHostAllowed">
    <iframe
      ref="iframe"
      :sandbox="sandboxPermission"
      :srcDoc="communicator"
      :src="communicatorSrc"
      :allow="allowPermission"
      @load="setupTracker"
    />
  </div>
</template>

<script lang="ts">
/**
 * PscManager is responsible for constructing a sandbox environment for PSC SDK to live in.
 * The sandbox is implemented using an iFrame with sandbox properties set to only [allow-scripts].
 *   - Communication after construction is done solely via postMessage.
 *
 * This component provides a public methods `handleEvent` to receive event externally.
 * Other components may call this method directly to communicate with the PSC SDK.
 */
import Vue, { PropType } from 'vue';
import PscCommandMaker from './PscCommandMaker';
import AgentFnMap, { AgentFnMapKey } from './agentFnMap';
import { Model } from '@viddsee/server-api-client';
import { Nullable } from '@/scripts/models/utility-types';

interface ComponentData {
  sandbox: string[];
  allow: string[];
  isThisHostAllowed: boolean;

  receiveMessage?(event: MessageEvent): void;
  initializeMessage?: string;
  pscCmdMaker?: PscCommandMaker;

  psc?: {
    mediaId: 'ViddseeWeb';
    regionId: 'sg';
    projectId: 'sg1';
    contentId: 'viddsee';
  };
  communicator?: Nullable<string>;
  communicatorSrc?: string;
  pscTracker?: string;
}

export default Vue.extend({
  props: {
    video: {
      type: Object as PropType<Model.Video.Info>,
      required: true,
    },
    player: {
      type: Object as PropType<jwplayer.JWPlayer>,
      required: true,
    },
    sandboxUrl: {
      type: String,
      default: 'https://hazelnut.viddsee.com',
    },
    strictSandbox: {
      type: Boolean,
      default: true,
    },
  },
  data(): ComponentData {
    return {
      sandbox: [
        'allow-scripts',
        ...(this.strictSandbox ? [] : ['allow-same-origin']),
      ],
      allow: [],
      /**
       * Hold true if the subdomain doesn't contain the string `berry`
       */
      isThisHostAllowed: !(location.host.split('.')[0].includes('berry')),
    };
  },
  computed: {
    sandboxPermission(): string {
      return this.sandbox.join(' ');
    },
    allowPermission(): string {
      return this.allow.join(' ');
    },
    sandboxUrlOrigin(): string {
      return this.strictSandbox ? '*' : new URL(this.sandboxUrl).origin;
    },
  },
  beforeDestroy() {
    if (this.receiveMessage) {
      window.removeEventListener('message', this.receiveMessage);
    }
    window.removeEventListener('beforeunload', this.sendBeforeUnloadEvent);
  },
  created() {
    // Disallow the rendering of this component during development
    // TODO: Need to develop a way to retreive this information without having
    // to manage and expose the url of our development environment
    if (!this.isThisHostAllowed) return;

    // Begin setting up this component
    this.initializeMessage = 'initialized';
    this.receiveMessage = (event): void => {
      const canHandle = ((): boolean => {
        if (event.source !== (this.$refs.iframe as HTMLIFrameElement).contentWindow) { return false; }

        if (this.strictSandbox) { return (event.origin === 'null'); }

        return event.origin === this.sandboxUrlOrigin;
      })();

      if (!canHandle) return;

      if (event.data === this.initializeMessage) {
        this.handleEvent(AgentFnMapKey.Load, []);
      }
    };

    Object
      .keys(AgentFnMap)
      .forEach((eventName): void => {
        this.player.on((eventName as any), (...params) => {
          this.handleEvent(eventName as AgentFnMapKey, ...params);
        });
      })
    ;

    window.addEventListener('message', this.receiveMessage, false);
    window.addEventListener('beforeunload', this.sendBeforeUnloadEvent, false);

    this.pscCmdMaker = new PscCommandMaker(this.video);

    /* eslint no-useless-escape: 0 */
    this.psc = {
      mediaId: 'ViddseeWeb',
      regionId: 'sg',
      projectId: 'sg1',
      contentId: 'viddsee',
    };

    /**
     * This communicator HTML template is used to setup
     * communication between this PscManager and the created sandbox.
     *
     * This communicator has a `receiveMessage` that receive message and execute.
     * The message it receive is an Object containing { `instruction`, `payload` }.
     * For convenience, this additional logics are contains inside the `methods`
     * object.
     *
     * This communicator will inject a variable `pigeon` which will later hold
     * a reference to the postMessage `event.source`.
     * This is done so that, to provide a way for other script in this sandbox to
     * communicate back to this PscManager.
     */
    this.communicator = null;
    const communicatorSrc = new URL(this.sandboxUrl);
    Object.entries(this.psc)
      // Exclude key set in the provided sandbox URL
      .filter(([key]) => !communicatorSrc.searchParams.has(key))
      .forEach(entry => communicatorSrc.searchParams.append(...entry))
    ;

    communicatorSrc.searchParams.append('parent', window.location.origin);

    this.communicatorSrc = communicatorSrc.href;

    /**
     * This pscTracker HTML template is used to setup the PSC SDK.
     * This code is copied from a documentation provided.
     *
     * Additional code `tag.addEventListener('load')` is added to track
     * the onload event of this sdk.
     */
    this.pscTracker = `
      var contentId = "${this.psc.contentId}";
      var initializeMessage = "${this.initializeMessage}";

      var gfkSsaConf = {
        "media": "${this.psc.mediaId}",
        "url": "//${this.psc.regionId}-config.sensic.net/${this.psc.projectId}-ssa-w.js"
      };
      ( function (w, d, c, s, id) {
        if (d.getElementById(id)) {return;}

        w["gfk"] = w["gfk"] || {};
        w["gfk"]["ssa"] = w["gfk"]["ssa"] || {};
        w["gfk"]["ssa"].agents = w["gfk"]["ssa"].agents || [];
        w["gfk"]["ssa"].getAgent = function() {
          var agent = { "queue": [], "a": arguments };
          agent.notifyLoaded = function() {
            agent.queue.push({"f": "notifyLoaded",
            "t": new Date().getTime(),"a": arguments});
          };
          agent.notifyPlay = function() {
            agent.queue.push({"f": "notifyPlay",
            "t": new Date().getTime(), "a": arguments});
          };
          agent.notifyIdle = function() {
            agent.queue.push({"f": "notifyIdle",
            "t": new Date().getTime(),"a": arguments});
          };
          agent.notifySkipped = function() {
            agent.queue.push({"f": "notifySkipped",
            "t": new Date().getTime(), "a": arguments});
          };
          w["gfk"]["ssa"].agents.push(agent);

          return agent;
        };

        var tag = d.createElement(s);
        var el = d.getElementsByTagName(s)[0];
        tag.id = id;
        tag.async = true;
        tag.type = 'text/javascript';
        tag.src = c.url;

        tag.addEventListener('load', function() {
          if (pigeon) {
            pigeon.postMessage(initializeMessage, '${window.location.origin}');
          }
        });

        el.parentNode.insertBefore(tag, el);
      })(window, document, gfkSsaConf, 'script', 'gfkSsa');
    `;
  },
  methods: {
    /**
     * A public methods ~provided by this component to fascilitate sending of
     * event into PSC SDK.
     * @param {String} eventName - name of the event coming from jwplayer
     * @param {...any} payload - arguments from the event
     */
    handleEvent(eventName: AgentFnMapKey, ...payload: any[]): void {
      const sandboxFrame = this.$refs.iframe as (HTMLFrameElement | undefined);

      // handle event only if sandbox exist
      if (sandboxFrame) {
        const sbWin = sandboxFrame.contentWindow || sandboxFrame;

        const appendedPayload = [
          ...payload,
          {
            position: this.player.getPosition(),
            type: 'position',
          },
        ];
        const message = this.pscCmdMaker && this.pscCmdMaker.filterPscEventMaker(eventName, ...appendedPayload);
        if (sbWin && message) {
          (sbWin as Window).postMessage(message, this.sandboxUrlOrigin);
        }
      }
    },
    sendBeforeUnloadEvent(): void {
      this.handleEvent(AgentFnMapKey.Pause);
    },

    // Setup tracker
    setupTracker(): void {
      const sandboxFrame = this.$refs.iframe as (HTMLFrameElement | undefined);
      const sbWin = (sandboxFrame && sandboxFrame.contentWindow) || sandboxFrame;

      // Send one initial message to trigger pigeon setup
      (sbWin as Window).postMessage({}, this.sandboxUrlOrigin);

      // Inject PCS SDK
      (sbWin as Window).postMessage({
        instruction: 'createScript',
        payload: this.pscTracker,
      }, this.sandboxUrlOrigin);
    },
  },
});
</script>

<style scoped lang="scss">
iframe {
  width: 0;
  height: 0;
  position: absolute;
  top: 0;
  left: -9999px;
  visibility: hidden;
  overflow: hidden;
}
</style>
